import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import api from '../services/api';

const EMPTY_SIZES = {
  logo_max_width: 200,
  logo_max_height: 40,
  signature_max_width: 180,
  signature_max_height: 48,
};

const SIZE_FIELDS = [
  { key: 'logo_max_width', label: 'Logo max width (px)' },
  { key: 'logo_max_height', label: 'Logo max height (px)' },
  { key: 'signature_max_width', label: 'Signature max width (px)' },
  { key: 'signature_max_height', label: 'Signature max height (px)' },
];

export default function BrandingSettings() {
  const [branding, setBranding] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [signatoryFile, setSignatoryFile] = useState(null);
  const [sizes, setSizes] = useState(EMPTY_SIZES);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getBranding();
      setBranding(res.data);
      if (res.data?.sizes) setSizes({ ...EMPTY_SIZES, ...res.data.sizes });
    } catch {
      setError('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    setBusy(true); setError(''); setSuccess('');
    try {
      const res = await fn();
      // Upload/size endpoints return the full branding state; use it directly.
      // Remove endpoints return a message, so reload instead.
      if (res?.data?.sizes) {
        setBranding(res.data);
        setSizes({ ...EMPTY_SIZES, ...res.data.sizes });
      } else {
        await load();
      }
      setSuccess(msg);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = () => {
    if (!logoFile) return;
    const fd = new FormData();
    fd.append('file', logoFile);
    run(() => api.uploadLogo(fd), 'Logo uploaded');
  };

  const uploadSignatory = () => {
    if (!signatoryFile) return;
    const fd = new FormData();
    fd.append('file', signatoryFile);
    run(() => api.uploadSignatory(fd), 'Signatory uploaded');
  };

  const saveSizes = () => {
    const payload = {};
    for (const { key } of SIZE_FIELDS) {
      const v = sizes[key];
      if (v !== '' && v !== null && v !== undefined) payload[key] = Number(v);
    }
    run(() => api.updateSizes(payload), 'Sizes updated');
  };

  const onSizeChange = (key) => (e) => {
    const v = e.target.value;
    setSizes((prev) => ({ ...prev, [key]: v === '' ? '' : Math.max(1, Number(v)) }));
  };

  const renderAssetCard = (kind) => {
    const isLogo = kind === 'logo';
    const title = isLogo ? 'Academy Logo' : 'Authorized Signatory (Signature)';
    const description = isLogo
      ? 'Placed where the {Logo} token appears in a template (usually the header).'
      : 'Placed where the {Signatory_Signature} token appears (usually the footer).';
    const asset = isLogo ? branding?.logo : branding?.signatory;
    const file = isLogo ? logoFile : signatoryFile;
    const setFile = isLogo ? setLogoFile : setSignatoryFile;
    const onUpload = isLogo ? uploadLogo : uploadSignatory;
    const onRemove = isLogo ? () => run(() => api.removeLogo(), 'Logo removed') : () => run(() => api.removeSignatory(), 'Signatory removed');

    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-32 items-center justify-center rounded-lg border border-border bg-secondary/40 overflow-hidden">
              {asset?.data_uri ? (
                <img src={asset.data_uri} alt={title} className="max-h-20 max-w-[8rem] object-contain" />
              ) : (
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {asset?.original_name ? (
                <p>Current: {asset.original_name}</p>
              ) : (
                <p>No image uploaded yet.</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`${kind}-file`}>Choose image (.png, .jpg, .jpeg, .gif — max 5 MB)</Label>
            <Input
              id={`${kind}-file`}
              type="file"
              accept=".png,.jpg,.jpeg,.gif"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={onUpload} disabled={!file || busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Upload
            </Button>
            <Button variant="outline" onClick={onRemove} disabled={!asset || busy}>
              <Trash2 className="mr-2 h-4 w-4" />
              Remove
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Branding</h1>
          <p className="text-sm text-muted-foreground">
            Upload the academy logo and authorized signatory. They appear in PDFs wherever the
            templates use the <code className="text-primary">{'{Logo}'}</code> and{' '}
            <code className="text-primary">{'{Signatory_Signature}'}</code> tokens.
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          {renderAssetCard('logo')}
          {renderAssetCard('signatory')}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Size limits</CardTitle>
            <CardDescription>
              Maximum dimensions for the injected images (aspect ratio is always preserved).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              {SIZE_FIELDS.map(({ key, label }) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={key}>{label}</Label>
                  <Input
                    id={key}
                    type="number"
                    min={1}
                    value={sizes[key]}
                    onChange={onSizeChange(key)}
                  />
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button onClick={saveSizes} disabled={busy}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save sizes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
